import { DateTime } from '@mode-switch/express';
import { ChartFacade } from '@mode/charts/contract';
import {
  FLAMINGO_RESERVED_REFS,
  getFlamingoRawDataFromDataset,
  isPresent,
  makePagingResult,
  prepareDataForCopying,
  shareReplayDefault,
} from '@mode/shared/util-js';
import { Switch } from '@mode/shared/util-switch';
import { DataViewFacade, FlamingoFacade } from '@mode/switch-views/contract';
import { BehaviorSubject, combineLatest as staticCombineLatest } from 'rxjs';
import { combineLatest, filter, map, startWith, switchMap } from 'rxjs/operators';
import { reportContextSelector } from '~/reducers/selectors';
import { ChartDesignerSelectors } from '~/selectors/designer.selectors';
import { ExplorerSelectors } from '../selectors/explorer.selectors';
import { AppSelect } from '../types/main.types';
import { VizExplorerObservables } from '../types/viz-designer.types';

/**
 * Make the GF observables object for `viz-viewer`.
 */
export function makeVizExplorerObservables(
  store: AppSelect,
  dataViewFacade: DataViewFacade,
  flamingoFacade: FlamingoFacade,
  chartFacade: ChartFacade
): VizExplorerObservables {
  // Note - here the token represents the reserved ref, and _not_ the vizToken.
  const token$ = new BehaviorSubject(FLAMINGO_RESERVED_REFS.ChartExplorer);

  /**
   * Viz based items.
   */

  const switchViewToken$ = token$.pipe(
    switchMap((token) => chartFacade.createSwitchViewTokenFromVizToken(token).pipe(filter(isPresent)))
  );

  const queryToken$ = switchViewToken$.pipe(
    switchMap((token) => dataViewFacade.createQueryTokenBySwitchViewToken(token))
  );

  const switchViewName$ = switchViewToken$.pipe(
    switchMap((token) => dataViewFacade.getSwitchViewNameBySwitchViewToken(token))
  );

  const isPurged$ = switchViewToken$.pipe(switchMap((token) => dataViewFacade.getIsPurgedBySwitchViewToken(token)));

  const switchViewLastSuccessfulRunAt$ = switchViewToken$.pipe(
    switchMap((token) => dataViewFacade.getDatasetResultCompletedAtBySwitchViewToken(token))
  );

  const queryRunToken$ = queryToken$.pipe(
    switchMap((queryToken) => dataViewFacade.createQueryRunTokenByQueryTokenNoEmpty(queryToken))
  );

  const title$ = chartFacade.createChartTitleFromVizToken(FLAMINGO_RESERVED_REFS.ChartExplorer);

  const chartType$ = chartFacade
    .createChartTypeFromVizToken(FLAMINGO_RESERVED_REFS.ChartExplorer)
    .pipe(filter(isPresent));

  const encoding$ = chartFacade
    .createEncodingFromVizToken(FLAMINGO_RESERVED_REFS.ChartExplorer)
    .pipe(filter(isPresent));

  const format$ = chartFacade
    .createChartFormatFromVizToken(FLAMINGO_RESERVED_REFS.ChartExplorer)
    .pipe(filter(isPresent));

  const fieldFormats$ = chartFacade
    .createChartFieldFormatMapFromVizToken(FLAMINGO_RESERVED_REFS.ChartExplorer)
    .pipe(filter(isPresent));

  const fieldColors$ = chartFacade
    .createChartFieldColorMapFromVizToken(FLAMINGO_RESERVED_REFS.ChartExplorer)
    .pipe(filter(isPresent));

  const colorPaletteToken$ = chartFacade
    .createPaletteFromVizToken(FLAMINGO_RESERVED_REFS.ChartExplorer)
    .pipe(filter(isPresent));

  const facetSizes$ = chartFacade
    .createChartFacetSizeMapFromVizToken(FLAMINGO_RESERVED_REFS.ChartExplorer)
    .pipe(filter(isPresent));

  const isAutoUpdateDisabled$ = chartFacade.createIsAutoUpdateDisabledFromVizToken(
    FLAMINGO_RESERVED_REFS.ChartExplorer
  );

  const manuallyApplyChanges$ = chartFacade.createManuallyApplyChangesFromVizToken(
    FLAMINGO_RESERVED_REFS.ChartExplorer
  );

  const isAutoOrManualRender$ = staticCombineLatest([isAutoUpdateDisabled$, manuallyApplyChanges$]).pipe(
    map(([isAutoUpdateDisabled, manuallyApplyChanges]) => !isAutoUpdateDisabled || manuallyApplyChanges)
  );

  // The unappliedChangesCount may be negative if the changes are a result of the user undoing
  // previously applied changes. This observable maps to the absolute value so that the consumers
  // just see the raw number of changes.
  const unappliedChangesCount$ = chartFacade
    .createUnappliedChangesFromVizToken(FLAMINGO_RESERVED_REFS.ChartExplorer)
    .pipe(map((unappliedChanges) => Math.abs(unappliedChanges)));

  const caption$ = chartFacade.createCaptionFromVizToken(FLAMINGO_RESERVED_REFS.ChartExplorer);

  /**
   * Run related items.
   */

  const runState$ = queryToken$.pipe(
    switchMap((queryToken) => dataViewFacade.createQueryRunStateByQueryTokenNoEmpty(queryToken))
  );

  const runFailed$ = runState$.pipe(
    map((state) => state === 'failed'),
    shareReplayDefault()
  );

  /**
   * Plan related items.
   */

  const explorerPlanId$ = flamingoFacade.createPlanIdFromRefId(FLAMINGO_RESERVED_REFS.ChartExplorer);

  const binSizePlanId$ = flamingoFacade.createPlanIdFromRefId(
    `${FLAMINGO_RESERVED_REFS.BinSize}#${FLAMINGO_RESERVED_REFS.ChartExplorer}`
  );

  const planSucceeded$ = explorerPlanId$.pipe(switchMap((planId) => flamingoFacade.createReadyFromPlanId(planId)));

  const planFailed$ = explorerPlanId$.pipe(switchMap((planId) => flamingoFacade.createFailedByPlanId(planId)));

  const planId$ = token$.pipe(
    switchMap((token) =>
      flamingoFacade
        .createPlanIdFromRefId(FLAMINGO_RESERVED_REFS.ChartExplorer)
        .pipe(map((planId) => ({ token, planId })))
    )
  );

  const planError$ = planId$.pipe(
    switchMap(({ planId }) => flamingoFacade.createErrorFromPlanId(planId)),
    shareReplayDefault()
  );

  // TODO - This will need to be migrated to use the new paging feature before this file
  //   can be moved to an observable creator in a component facade as we'll need to remove
  //   the legacy `ChartDesignerSelectors` usage.
  const paging$ = planId$.pipe(
    switchMap(({ planId, token }) =>
      staticCombineLatest([
        store.select(ChartDesignerSelectors.createPagingSelector(token)),
        flamingoFacade.createCountFromPlanId(planId).pipe(filter(isPresent)),
      ]).pipe(map(([paging, count]) => makePagingResult(paging, count)))
    ),
    filter((p) => p != null)
  );

  /**
   * View/model.
   */

  const model$ = dataViewFacade.model$;

  // @TODO: (@anthonysimone) [GF] refactor this to use `switchViewToken` once that is added as a new chart state key
  const view$ = staticCombineLatest([queryToken$, model$]).pipe(
    map(([queryToken, model]) => Switch.Views.find(model, queryToken)),
    shareReplayDefault()
  );

  /**
   * Loading and state related items.
   */

  const isValid$ = chartFacade.createIsValidFromVizToken(FLAMINGO_RESERVED_REFS.ChartExplorer);

  const isComplete$ = chartFacade.createIsCompleteFromVizToken(FLAMINGO_RESERVED_REFS.ChartExplorer);

  const canRender$ = staticCombineLatest([isValid$, isComplete$]).pipe(
    map(([isValid, isComplete]) => isValid && isComplete)
  );

  const planLoading$ = explorerPlanId$.pipe(
    switchMap((planId) => flamingoFacade.createLoadingByPlanId(planId)),
    combineLatest(canRender$),
    map(([loading, canRender]) => loading && canRender)
  );

  const binSizePlanLoading$ = binSizePlanId$.pipe(switchMap((planId) => flamingoFacade.createLoadingByPlanId(planId)));

  const planPageLoading$ = explorerPlanId$.pipe(
    switchMap((planId) => flamingoFacade.createPageLoadingByPlanId(planId)),
    combineLatest(canRender$),
    map(([loading, canRender]) => loading && canRender)
  );

  const queryExists$ = queryToken$.pipe(map((token) => !!token));

  // This observable is piped off of `queryExists$` to demonstrate that it's only valid to use this
  // if we can assume that the query already exists. If the query doesn't exist, this will never resolve
  // to `true`.
  const isViewLoaded$ = queryExists$.pipe(
    combineLatest(view$, (queryExists, view) => {
      if (queryExists && view !== undefined) {
        return true;
      }
    }),
    filter((isLoaded) => isLoaded !== undefined),
    startWith(false)
  );

  /**
   * Composed items - the rest are either results or composed of data from multiple sources.
   */

  // @TODO: using the selector because the facade
  const chartData$ = planId$.pipe(
    switchMap(({ planId, token }) =>
      chartFacade.createFlamingoChartSummary(token, planId).pipe(
        filter((result) => result !== undefined) // nulls should pass through
        // first() // @TODO: Temporarily removed `first()` as this observable from the facade doesn't behave
        // exactly the same as the selector taken directly.
      )
    )
  );

  const datasetForCopy$ = chartData$.pipe(map((data) => prepareDataForCopying(getFlamingoRawDataFromDataset(data))));

  // TODO: Migrate this selector at some point
  const chartState$ = store.selectNoEmpty(ExplorerSelectors.getChartStateGf).pipe(
    filter((c) => c.version === 2) // Non-charts will not have their state filled out
  );

  const renderModel$ = planId$.pipe(switchMap(({ token, planId }) => chartFacade.createRenderDataModel(token, planId)));

  const columns$ = flamingoFacade.createColumnsByRefId(FLAMINGO_RESERVED_REFS.ChartExplorer);

  const dataProfiling$ = token$.pipe(
    switchMap((token) => flamingoFacade.createDataProfilingFromVizToken(token).pipe(filter(isPresent)))
  );

  const binSizeToFieldMap$ = token$.pipe(
    switchMap((token) => flamingoFacade.createBinSizeToFieldMapFromVizToken(token).pipe(filter(isPresent)))
  );

  const conditionalFormatting$ = token$.pipe(
    switchMap((token) => flamingoFacade.createConditionalFormattingFromVizToken(token).pipe(filter(isPresent)))
  );

  const defaultYearStart$ = store
    .select(reportContextSelector)
    .pipe(
      map((reportContext) => DateTime.yearStart(reportContext.settings?.['year_start']) ?? DateTime.YearStart.January)
    );

  const defaultWeekStart$ = store
    .select(reportContextSelector)
    .pipe(
      map((reportContext) => DateTime.weekStart(reportContext.settings?.['week_start']) ?? DateTime.WeekStart.Sunday)
    );

  return {
    token$,
    planId$,
    chartType$,
    queryToken$,
    queryRunToken$,
    switchViewToken$,
    encoding$,
    format$,
    fieldFormats$,
    fieldColors$,
    colorPaletteToken$,
    facetSizes$,
    isAutoUpdateDisabled$,
    manuallyApplyChanges$,
    isAutoOrManualRender$,
    unappliedChangesCount$: unappliedChangesCount$,
    title$,
    caption$,
    isValid$,
    isComplete$,
    canRender$,
    runFailed$,
    view$,
    chartState$,
    model$,
    chartData$,
    datasetForCopy$,

    planError$,
    planLoading$,
    planPageLoading$,
    planFailed$,
    planSucceeded$,

    renderModel$,
    columns$,
    paging$,

    queryExists$,
    isViewLoaded$,
    switchViewName$,
    switchViewLastSuccessfulRunAt$,
    dataProfiling$,
    binSizeToFieldMap$,
    conditionalFormatting$,
    binSizePlanLoading$,

    defaultYearStart$,
    defaultWeekStart$,
    isPurged$,
  };
}
